import { useRouter } from 'vue-router';
import _ from 'lodash';
import { Site, SLD } from '@patsnap-utils/biz';
import { Toast } from 'patsnap-biz';
import { useI18n } from 'vue-i18n';
import { openMessageBox } from '@eureka/confirm-box';
import { HistoryType, MenuPath, type HistoryItem } from '../types';
import { removeHistory, removeAllRecords } from '../infrastructure/api-functions';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import { cn, tw, en, jp } from '../infrastructure/locales';
import { useNavBarState } from './use-navbar-store';
import { getPath, JUMP_PACKAGE, EVENTS_CONFIG } from '../constants';

export const useNavBar = () => {
    const { t } = useI18n({
        messages: {
            cn, en, jp, tw,
        },
    });

    const router = useRouter();

    const { historyList, updateHistory } = useNavBarState();

    function confirmSaveBoardChange(data: { title?: string; content?: string } = {}) {
        return openMessageBox({
            title: data.title || t('NavBar.deleteTitle'),
            message: data.content || t('NavBar.deleteContent'),
            confirmButtonText: t('NavBar.confirm'),
            cancelButtonText: t('NavBar.cancel'),
        });
    }

    async function removeHistoryItem(data: HistoryItem, filters: string[] = []) {
        if (data.active) return;
        const confirm = await confirmSaveBoardChange()
        if (!confirm) return
        removeHistory(data, filters).then(() => {
            updateHistory(_.filter(historyList.value, nav => nav.id !== data.id));
            return Toast({
                message: t('NavBar.deleteSuccess'),
                type: 'success',
            });
        })
    }

    function track(type: string, detail: string) {
        CommonApis.track({
            event_type: type,
            event_detail_str: detail,
            module_type: 'EUREKA',
            source_type: 'air',
        })
    }

    function navigateTo(menu: HistoryItem) {
        const path = getPath(menu.id, menu.source)
        const packageName = JUMP_PACKAGE[menu.source || menu.id]
        const isFullUrl = /^(http|https):\/\//.test(packageName)
        if (isFullUrl) {
            return window.open(packageName, menu.target || '_self')
        }
        let url = new Site({ sld: SLD.EUREKA, pathname: packageName + path }).href
        if (menu.source === HistoryType.SEEK_SEARCH) {
            url = new Site({ sld: SLD.EUREKA, pathname: `page/${menu.id}` }).href
        }
        if (url) {
            window.open(url, menu.target || '_self')
        }
    }

    function openSeekQuestion() {
        (document.querySelector('#new_question_modal') as HTMLDialogElement)?.showModal()
    }

    function viewResult(menu) {
        const event = EVENTS_CONFIG[menu.type || menu.id];
        // 技术信息问答时打开提问弹窗
        if (menu.id === MenuPath.SEEK) {
            openSeekQuestion();
        } else {
            navigateTo(menu);
        }
        track(event, menu.id);
    }

    async function removeAllHistory(id: string) {
        track(EVENTS_CONFIG[id], id);
        const confirm = await confirmSaveBoardChange({
            title: t('NavBar.deleteAllTitle'),
            content: t('NavBar.deleteAllContent'),
        })
        if (!confirm) return
        removeAllRecords().then(() => {
            updateHistory([]);
            Toast({
                message: t('NavBar.deleteSuccess'),
                type: 'success',
            });
        })
    }

    return {
        viewResult,
        removeHistoryItem,
        removeAllHistory,
    }
}
